import type { OrderStatus } from '@jalupro/shared';
import { useStatusLabel } from '../i18n';

/**
 * Status primitives ported from the clean (variant-B) wireframe
 * (design-source/wire-primitives.jsx). The wireframe used lowercase string keys;
 * here we key off the canonical `OrderStatus` enum from @jalupro/shared so screens
 * can pass an order's real status straight through.
 */

type Tone = 'blue' | 'amber' | 'green' | 'red' | 'muted';

interface StatusMeta {
  readonly tone: Tone;
  readonly label: string;
  /** Pipeline index for the linear happy path (-1 = off the linear pipeline). */
  readonly pipelineIdx: number;
}

/**
 * The linear happy-path pipeline (matches the wireframe STATUSES + the order
 * state-machine happy path). ON_HOLD_ADMIN_REVIEW and DELIVERY_PROBLEM are
 * intentionally off this linear strip (branches).
 */
export const STATUSES: ReadonlyArray<{ key: OrderStatus; label: string }> = [
  { key: 'NEW', label: 'New' },
  { key: 'PROFORMA_SENT', label: 'Proforma sent' },
  { key: 'PAID', label: 'Paid' },
  { key: 'INVOICED', label: 'Invoiced' },
  { key: 'SHIPPED', label: 'Shipped' },
  { key: 'DELIVERED', label: 'Delivered' },
];

const STATUS_META: Record<OrderStatus, StatusMeta> = {
  NEW: { tone: 'blue', label: 'New', pipelineIdx: 0 },
  PROFORMA_SENT: { tone: 'amber', label: 'Proforma sent', pipelineIdx: 1 },
  ON_HOLD_ADMIN_REVIEW: { tone: 'red', label: 'On hold · review', pipelineIdx: -1 },
  PAID: { tone: 'green', label: 'Paid', pipelineIdx: 2 },
  INVOICED: { tone: 'green', label: 'Invoiced', pipelineIdx: 3 },
  SHIPPED: { tone: 'blue', label: 'Shipped', pipelineIdx: 4 },
  DELIVERED: { tone: 'green', label: 'Delivered', pipelineIdx: 5 },
  DELIVERY_PROBLEM: { tone: 'red', label: 'Delivery problem', pipelineIdx: -1 },
};

/** Index of a status on the linear pipeline strip (for `Pipeline activeIdx`). */
export function statusPipelineIndex(status: OrderStatus): number {
  return STATUS_META[status]?.pipelineIdx ?? 0;
}

export interface StatusPillProps {
  status: OrderStatus;
}

/** Coloured status pill (clean `.cl .pill.dot` variant). */
export function StatusPill({ status }: StatusPillProps) {
  const statusLabel = useStatusLabel();
  const meta = STATUS_META[status] ?? { tone: 'muted' as const };
  return <span className={`pill dot ${meta.tone}`}>{statusLabel(status)}</span>;
}

type StepState = 'done' | 'active' | 'todo' | 'problem';

/** Glyph shown inside each step dot. The active step shows an animated spinner via
 *  CSS (::after), so its glyph is intentionally empty. */
const STEP_ICON: Record<StepState, string> = {
  done: '✓',
  active: '',
  problem: '!',
  todo: '',
};

/**
 * Derive the pipeline view from an order status: where it sits on the linear strip,
 * whether the current step is a problem, and whether the order is fully complete
 * (DELIVERED → every step renders done/green with a check).
 */
export function pipelineView(status: OrderStatus): {
  activeIdx: number;
  problem: boolean;
  complete: boolean;
} {
  switch (status) {
    case 'NEW':
      return { activeIdx: 0, problem: false, complete: false };
    case 'PROFORMA_SENT':
      return { activeIdx: 1, problem: false, complete: false };
    case 'PAID':
      return { activeIdx: 2, problem: false, complete: false };
    case 'INVOICED':
      return { activeIdx: 3, problem: false, complete: false };
    case 'SHIPPED':
      return { activeIdx: 4, problem: false, complete: false };
    case 'DELIVERED':
      return { activeIdx: 5, problem: false, complete: true };
    // Held for review after the proforma — flag that step as a problem.
    case 'ON_HOLD_ADMIN_REVIEW':
      return { activeIdx: 1, problem: true, complete: false };
    // Shipped but delivery failed — everything up to delivery done, delivery is the problem.
    case 'DELIVERY_PROBLEM':
      return { activeIdx: 5, problem: true, complete: false };
    default:
      return { activeIdx: 0, problem: false, complete: false };
  }
}

export interface PipelineProps {
  /** Order status — drives step states, icons, and the completed/problem cases. */
  status?: OrderStatus;
  /** Legacy: explicit current step (0-based) when no status is passed. */
  activeIdx?: number;
  compact?: boolean;
}

/** Horizontal status pipeline (clean `.cl .pipeline` variant). */
export function Pipeline({ status, activeIdx, compact = false }: PipelineProps) {
  const steps = STATUSES;
  const statusLabel = useStatusLabel();
  const view = status
    ? pipelineView(status)
    : { activeIdx: activeIdx ?? 2, problem: false, complete: false };

  return (
    <div className={`pipeline ${compact ? 'compact' : ''}`} style={{ width: '100%' }}>
      {steps.map((s, i) => {
        const state: StepState = view.complete
          ? 'done'
          : i < view.activeIdx
            ? 'done'
            : i === view.activeIdx
              ? view.problem
                ? 'problem'
                : 'active'
              : 'todo';
        const lineDone = view.complete || i < view.activeIdx;
        return (
          <div key={s.key} style={{ display: 'contents' }}>
            <div className={`step ${state}`}>
              <div className="dot" aria-hidden>
                {STEP_ICON[state]}
              </div>
              <div className="cap">{statusLabel(s.key)}</div>
            </div>
            {i < steps.length - 1 && <div className={`line ${lineDone ? '' : 'todo'}`} />}
          </div>
        );
      })}
    </div>
  );
}
